import React, { type FC, useMemo } from 'react';
import { Box, type SxProps, type Theme } from '@mui/material';
import camelCase from 'lodash/camelCase';

import { borders } from '@/theme/constants';

type EvaluationColor = {
  color: string;
  bgColor: string;
};

const DEFAULT_COLORS: EvaluationColor = {
  color: 'grey.900',
  bgColor: 'grey.100'
};

const EVALUATION_COLOR_MAP: Record<string, EvaluationColor> = {
  recommended: { color: 'green.900', bgColor: 'green.200' },
  lowRisk: { color: 'green.900', bgColor: 'green.200' },
  inquiryRequired: { color: 'green.700', bgColor: 'green.100' },
  marginal: { color: 'orange.main', bgColor: 'orange.300' },
  highRisk: { color: 'orange.main', bgColor: 'orange.300' },
  moderateRisk: { color: 'orange.main', bgColor: 'orange.300' },
  notRecommended: { color: 'red.main', bgColor: 'red.300' },
  noEvaluationAvailable: { color: 'grey.900', bgColor: 'grey.100' },
  noEvaluation: { color: 'grey.900', bgColor: 'grey.100' },
  veryLowRisk: { color: 'primary.600', bgColor: 'primary.100' }
};

function getEvaluationColors(evaluation: string): EvaluationColor {
  const normalized = camelCase(evaluation ?? '');

  return EVALUATION_COLOR_MAP[normalized] ?? DEFAULT_COLORS;
}

export interface EvaluationChipProps {
  evaluation: string | null | undefined;
  sx?: SxProps<Theme>;
}

const EvaluationChip: FC<EvaluationChipProps> = ({ evaluation, sx }) => {
  const colors = useMemo(() => getEvaluationColors(evaluation ?? ''), [evaluation]);

  if (!evaluation) return null;

  return (
    <Box
      sx={{
        py: 0.25,
        px: 0.5,
        borderRadius: 1,
        border: borders.half,
        borderColor: colors.color,
        bgcolor: colors.bgColor,
        color: colors.color,
        fontSize: '10px',
        ...sx
      }}
    >
      {evaluation}
    </Box>
  );
};

export default EvaluationChip;
